import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  LinearProgress,
  Paper,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import { 
  CheckCircle, 
  CloudUpload, 
  Delete, 
  Description,
  Image as ImageIcon,
  PictureAsPdf,
  AttachFile,
} from '@mui/icons-material';
import { debounce } from 'lodash';
import {
  PatientAssessment,
  AssessmentTemplate,
  assessmentAPI,
  calculateProgress,
  isAssessmentComplete,
} from '../../services/assessmentService';

interface DocumentUploadAssessmentProps {
  assessment: PatientAssessment;
  template: AssessmentTemplate;
  onComplete: () => void;
  onError: (error: string) => void;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: string;
  url?: string;
}

const DocumentUploadAssessment: React.FC<DocumentUploadAssessmentProps> = ({
  assessment,
  template,
  onComplete,
  onError
}) => {
  const [responses, setResponses] = useState<Record<string, string>>(assessment.responses || {});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(assessment.progress || 0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Supported file types
  const supportedTypes = [
    'application/pdf',
    'image/jpeg', 
    'image/jpg',
    'image/png',
    'image/gif',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain'
  ];

  const maxFileSize = 10 * 1024 * 1024; // 10MB
  const maxFiles = 10;

  // Auto-save debounced function
  const debouncedSave = useCallback(
    debounce(async (responses: Record<string, string>, progress: number) => {
      try {
        setSaving(true);
        await assessmentAPI.saveResponses(assessment.assessmentId, {
          responses,
          progress,
          status: progress > 0 ? 'in_progress' : 'assigned'
        });
      } catch (error) {
      } finally {
        setSaving(false);
      }
    }, 2000),
    [assessment.assessmentId]
  );

  // Load existing uploaded files
  useEffect(() => {
    const filesData = responses['uploaded_files'];
    if (filesData) {
      try {
        const files = JSON.parse(filesData);
        setUploadedFiles(Array.isArray(files) ? files : []);
      } catch (error) {
        setUploadedFiles([]);
      }
    }
  }, []);

  // Update progress when files change
  useEffect(() => {
    const fileResponses = {
      ...responses,
      uploaded_files: JSON.stringify(uploadedFiles)
    };

    const newProgress = uploadedFiles.length > 0 ? 1 : 0; // Simple progress: 0 if no files, 1 if files uploaded
    setProgress(newProgress);

    // Auto-save if there are files
    if (uploadedFiles.length > 0) {
      setResponses(fileResponses);
      debouncedSave(fileResponses, newProgress);
    }
  }, [uploadedFiles, debouncedSave]);

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <PictureAsPdf color="error" />;
    if (fileType.includes('image')) return <ImageIcon color="info" />;
    if (fileType.includes('word') || fileType.includes('document')) return <Description color="primary" />;
    return <AttachFile color="action" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length === 0) return;

    // Check total file count
    if (uploadedFiles.length + files.length > maxFiles) {
      onError(`Maximum ${maxFiles} files allowed. You currently have ${uploadedFiles.length} files uploaded.`);
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of files) {
      if (!supportedTypes.includes(file.type)) {
        errors.push(`${file.name}: File type not supported`);
        continue;
      }

      if (file.size > maxFileSize) {
        errors.push(`${file.name}: File too large (max ${formatFileSize(maxFileSize)})`);
        continue;
      }

      validFiles.push(file);
    }

    if (errors.length > 0) {
      onError(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      uploadFiles(validFiles);
    }

    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFiles = async (files: File[]) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Update progress
        setUploadProgress(((i + 1) / files.length) * 100);

        // Simulate file upload (in real implementation, upload to S3 or backend)
        await new Promise(resolve => setTimeout(resolve, 1000));

        const uploadedFile: UploadedFile = {
          id: `file_${Date.now()}_${i}`,
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toISOString(),
          url: URL.createObjectURL(file) // In real implementation, this would be the S3 URL
        };

        setUploadedFiles(prev => [...prev, uploadedFile]);
      }

    } catch (error: any) {
      onError('Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId);
      return updated;
    });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      // Check if at least one file is uploaded (if required)
      const hasRequiredFiles = uploadedFiles.length > 0;
      const isRequired = template.questions.some(q => q.required && q.questionType === 'file_upload');
      
      if (isRequired && !hasRequiredFiles) {
        onError('Please upload at least one supporting document before submitting.');
        return;
      }

      // Submit assessment with file references
      await assessmentAPI.submitAssessment(assessment.assessmentId, {
        responses: {
          uploaded_files: JSON.stringify(uploadedFiles),
          file_count: uploadedFiles.length.toString()
        }
      });

      onComplete();
    } catch (error: any) {
      onError(error.response?.data?.message || 'Failed to submit assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isComplete = uploadedFiles.length > 0;

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CloudUpload sx={{ mr: 2, fontSize: 32, color: 'info.main' }} />
          <Box>
            <Typography variant="h5" gutterBottom>
              Supporting Documents
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload documents that support your FMLA application (medical records, physician notes, etc.)
            </Typography>
          </Box>
        </Box>

        {/* Progress */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Files uploaded: {uploadedFiles.length}
            </Typography>
            {saving && (
              <Typography variant="caption" color="text.secondary">
                Saving...
              </Typography>
            )}
          </Box>
        </Box>

        {/* Upload Area */}
        <Box sx={{ mb: 4 }}>
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              border: '2px dashed',
              borderColor: 'grey.300',
              bgcolor: 'grey.50',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.50'
              },
              cursor: 'pointer'
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Drop files here or click to upload
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Supported formats: PDF, JPG, PNG, DOC, DOCX, TXT
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Maximum file size: {formatFileSize(maxFileSize)} | Maximum {maxFiles} files
            </Typography>
          </Paper>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={supportedTypes.join(',')}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Box>

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Uploading files... {Math.round(uploadProgress)}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
        )}

        {/* Uploaded Files List */}
        {uploadedFiles.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Uploaded Files ({uploadedFiles.length})
            </Typography>
            <List>
              {uploadedFiles.map((file) => (
                <ListItem
                  key={file.id}
                  sx={{
                    border: 1,
                    borderColor: 'grey.200',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: 'background.paper'
                  }}
                >
                  <ListItemIcon>
                    {getFileIcon(file.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip label={formatFileSize(file.size)} size="small" variant="outlined" />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(file.uploadedAt).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton 
                      edge="end" 
                      onClick={() => handleRemoveFile(file.id)}
                      color="error"
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isComplete && (
              <CheckCircle color="success" fontSize="small" />
            )}
            <Typography variant="body2" color={isComplete ? 'success.main' : 'text.secondary'}>
              {isComplete 
                ? `${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''} uploaded` 
                : 'No files uploaded yet'
              }
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? null : <CheckCircle />}
            sx={{ minWidth: 200 }}
          >
            {submitting ? 'Submitting...' : 'Complete Document Upload'}
          </Button>
        </Box>

        {/* Help Text */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Document Guidelines:</strong> Upload any supporting medical documentation, physician notes, 
            test results, or other relevant documents that support your FMLA application. All documents are 
            securely encrypted and HIPAA compliant. You can upload files now or skip this step if you prefer 
            to provide documents during your consultation.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DocumentUploadAssessment;